import React, { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import QRCode from '../ui/QRCode';
import { supabase } from '../../lib/supabase';
import { API_BASE_URL } from '../../config/apiUrl';

interface ActivationCodeModalProps {
  isOpen: boolean;
  onClose: () => void;
  seniorName: string;
  seniorId: string;
  activationCode: string;
  /** D5 — device model, relocated here from the dashboard header. */
  deviceName?: string;
  /** Called with the fresh code after a successful regenerate so the dashboard
   *  can refresh its cached senior data. */
  onCodeRegenerated?: (newCode: string) => void;
}

export const ActivationCodeModal: FC<ActivationCodeModalProps> = ({
  isOpen,
  onClose,
  seniorName,
  seniorId,
  activationCode,
  deviceName,
  onCodeRegenerated,
}) => {
  const { t } = useTranslation();

  // Local copy of the code so the QR + text update immediately after a
  // regenerate, before the parent refetches.
  const [code, setCode] = useState(activationCode);
  const [confirming, setConfirming] = useState(false);
  const [working, setWorking] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      // Reset transient state each time the modal opens with (possibly new) data.
      setCode(activationCode);
      setConfirming(false);
      setError(null);
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen, activationCode]);

  const handleCopy = () => {
    navigator.clipboard.writeText(code);
  };

  const handleRegenerate = async () => {
    setWorking(true);
    setError(null);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;
      if (!token) {
        setError(t('activationModal.sessionExpired'));
        return;
      }

      const apiUrl = API_BASE_URL;
      const res = await fetch(`${apiUrl}/seniors/${seniorId}/regenerate-activation-code`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });

      if (res.status === 429) {
        setError(t('activationModal.rateLimited'));
        return;
      }
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        setError(body?.error || t('activationModal.regenerateFailed'));
        return;
      }

      const body = await res.json();
      if (!body?.activationCode) {
        setError(t('activationModal.regenerateFailed'));
        return;
      }

      setCode(body.activationCode);
      setConfirming(false);
      onCodeRegenerated?.(body.activationCode);
    } catch (err) {
      console.error('[ActivationCode] Regenerate failed:', err);
      setError(t('activationModal.regenerateFailed'));
    } finally {
      setWorking(false);
    }
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-50 bg-brand-text/50 backdrop-blur-sm animate-fadeIn"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none">
        <div
          className="bg-white rounded-2xl shadow-2xl w-full max-w-sm pointer-events-auto animate-slideUp"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between p-5 pb-0">
            <h2 className="font-display text-xl font-bold text-brand-text">
              {t('activationModal.title')}
            </h2>
            <button
              onClick={onClose}
              className="p-1.5 rounded-lg hover:bg-brand-bg-warm transition-colors"
              aria-label={t('activationModal.close')}
            >
              <span className="material-symbols-outlined text-xl text-brand-text-muted">close</span>
            </button>
          </div>
          <p className="px-5 text-sm text-brand-text-muted mt-1">
            For <span className="font-semibold text-brand-text">{seniorName}</span>
          </p>

          {/* QR Code */}
          <div className="flex flex-col items-center py-6 px-5">
            <div className="p-3 bg-white rounded-xl border border-brand-border shadow-sm">
              <QRCode value={code} size={180} />
            </div>

            {/* Code Display */}
            <div className="mt-5 flex items-center gap-2">
              <span className="text-2xl font-mono font-bold text-brand-text tracking-widest">
                {code}
              </span>
              <button
                onClick={handleCopy}
                className="p-1.5 rounded-lg hover:bg-brand-bg-warm text-brand-text-muted hover:text-brand-primary transition-colors"
                title={t('activationModal.copyCode')}
              >
                <span className="material-symbols-outlined text-lg">content_copy</span>
              </button>
            </div>

            <p className="text-xs text-brand-text-muted mt-3 text-center">
              {t('activationModal.instructions')}
            </p>

            {/* D5 (2026-08-10) — the phone model used to sit in the dashboard
                header, where it was noise for the caregiver. Support still needs
                to know which handset this is, so it lives here: this panel is
                already "the phone" (its pairing code and QR), which is where
                someone asks "what device are they on?". Rendered only once the
                device has reported a model over Firebase. */}
            {deviceName && (
              <p className="text-xs text-brand-text-muted/80 mt-4 flex items-center gap-1.5">
                <span className="material-symbols-outlined" style={{ fontSize: '14px' }}>
                  smartphone
                </span>
                {deviceName}
              </p>
            )}
          </div>

          {/* Footer */}
          <div className="p-5 pt-0 flex flex-col gap-3">
            {error && (
              <div className="p-3 rounded-lg bg-red-50 border border-red-200 text-sm text-red-700 flex items-start gap-2">
                <span className="material-symbols-outlined text-lg">error</span>
                <span>{error}</span>
              </div>
            )}

            {confirming ? (
              <div className="p-3 rounded-lg bg-amber-50 border border-amber-200">
                <p className="text-sm text-amber-900 leading-relaxed">
                  {t('activationModal.regenerateWarning', { name: seniorName })}
                </p>
                <div className="mt-3 flex gap-2">
                  <button
                    onClick={handleRegenerate}
                    disabled={working}
                    className="flex-1 py-2.5 text-sm font-semibold text-white bg-brand-primary rounded-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  >
                    {working ? (
                      <>
                        <span className="material-symbols-outlined text-lg animate-spin">progress_activity</span>
                        {t('activationModal.generating')}
                      </>
                    ) : (
                      t('activationModal.confirmReplace')
                    )}
                  </button>
                  <button
                    onClick={() => setConfirming(false)}
                    disabled={working}
                    className="flex-1 py-2.5 text-sm font-semibold text-brand-text-light bg-brand-bg-warm border border-brand-border rounded-lg hover:bg-brand-border/50 hover:text-brand-text transition-colors disabled:opacity-50"
                  >
                    {t('activationModal.cancel')}
                  </button>
                </div>
              </div>
            ) : (
              <button
                onClick={() => { setError(null); setConfirming(true); }}
                className="w-full py-2.5 text-sm font-semibold text-brand-primary bg-brand-primary/5 border border-brand-primary/30 rounded-lg hover:bg-brand-primary/10 transition-colors flex items-center justify-center gap-2"
              >
                <span className="material-symbols-outlined text-lg">autorenew</span>
                {t('activationModal.generateNew')}
              </button>
            )}

            <button
              onClick={onClose}
              className="w-full py-2.5 text-sm font-semibold text-brand-text-light bg-brand-bg-warm border border-brand-border rounded-lg hover:bg-brand-border/50 hover:text-brand-text transition-colors"
            >
              {t('activationModal.done')}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default ActivationCodeModal;
